import { motion } from "framer-motion";
import { Search, Target, Palette, Code2, Rocket } from "lucide-react";

const STEPS = [
    {
        icon: Search,
        step: "01",
        title: "Discovery",
        description: "We immerse ourselves in your business, users and goals — workshops, research and audits that surface what truly matters.",
        testid: "process-step-discovery",
    },
    {
        icon: Target,
        step: "02",
        title: "Strategy",
        description: "Insights become a sharp roadmap: scope, architecture, success metrics and a plan everyone believes in.",
        testid: "process-step-strategy",
    },
    {
        icon: Palette,
        step: "03",
        title: "Design",
        description: "Wireframes evolve into pixel-perfect interfaces and interactive prototypes — reviewed with you, not at you.",
        testid: "process-step-design",
    },
    {
        icon: Code2,
        step: "04",
        title: "Development",
        description: "Clean, tested, performant code. Weekly demos keep you close to the build from first commit to feature-complete.",
        testid: "process-step-development",
    },
    {
        icon: Rocket,
        step: "05",
        title: "Launch",
        description: "Deployment, monitoring, handover and post-launch care — we stay in your corner long after go-live.",
        testid: "process-step-launch",
    },
];

export const Process = () => {
    return (
        <section id="process" className="relative py-20 lg:py-32 bg-gradient-to-b from-transparent via-blue-50/40 to-transparent">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-5"
                >
                    <div className="lg:sticky lg:top-28">
                        <div className="font-mono-brand text-xs tracking-[0.25em] uppercase font-semibold text-violet-600 mb-4" data-testid="process-eyebrow">
                            How we work
                        </div>
                        <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-2xl sm:text-3xl lg:text-4xl leading-snug">
                            A process built for <span className="gradient-text">momentum.</span>
                        </h2>
                        <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                            Five deliberate stages, zero ambiguity. You always know where your product stands — and what's next.
                        </p>
                        <div className="mt-8 rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-6 shadow-lg shadow-slate-200/40">
                            <div className="font-mono-brand text-[11px] tracking-[0.2em] uppercase text-slate-400">Typical engagement</div>
                            <div className="font-display font-bold text-slate-900 text-xl mt-1.5">4–12 weeks, brief to launch</div>
                        </div>
                    </div>
                </motion.div>

                <div className="lg:col-span-7">
                    <div className="relative border-l-2 border-violet-200 ml-4 md:ml-6 pl-8 md:pl-12 space-y-10">
                        {STEPS.map((step, i) => (
                            <motion.div
                                key={step.step}
                                initial={{ opacity: 0, x: 40 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="relative"
                            >
                                <div className="absolute -left-[52px] md:-left-[68px] top-1 h-11 w-11 rounded-full gradient-bg shadow-lg shadow-violet-500/30 flex items-center justify-center">
                                    <step.icon size={18} className="text-white" />
                                </div>
                                <div
                                    data-testid={step.testid}
                                    className="rounded-2xl border border-white/60 bg-white/80 backdrop-blur-xl p-6 shadow-lg shadow-slate-200/40 hover:shadow-xl hover:shadow-violet-500/15 hover:-translate-y-1 transition-all duration-500"
                                >
                                    <div className="flex items-baseline gap-3">
                                        <span className="font-mono-brand text-xs font-semibold gradient-text">{step.step}</span>
                                        <h3 className="font-display font-bold text-lg text-slate-900">{step.title}</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed mt-2.5">{step.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
